import { dungApiThat, goiApi } from '../api/http';
import { MOCK_DASHBOARD, MOCK_KNOWLEDGE_BASES } from '../../mock/fixtures';
import { mockRequest } from '../../mock/transport';
import type { DashboardCounters, KnowledgeBaseDisplay } from '../../modules/dashboard/types';
import type { DocStats } from '../../modules/xu-ly/types';
import type { OcrSchemaStats } from '../../modules/cau-hinh-ocr/types';
import type { KnowledgeStats, Trang as TrangTriThuc } from '../../modules/tri-thuc/types';
import type { KnowledgeBase as KbTriThuc } from '../../modules/tri-thuc/types';
import type { Chatbot as ChatbotTroLy } from '../../modules/tro-ly-ai/types';
import type { UserPage } from '../../modules/nguoi-dung/types';
import type { DepartmentPage } from '../../modules/co-cau-to-chuc/types';
import type { RolePage } from '../../modules/vai-tro/types';

const GOC_OCR = '/api/v1/m/ocr';
const GOC_TRI_THUC = '/api/v1/m/tri-thuc';
const GOC_TRO_LY = '/api/v1/m/tro-ly-ai';

/** Số đếm rỗng, để diễn trạng thái `?mock=empty` của dashboard. */
const EMPTY_COUNTERS: DashboardCounters = {
  tongChungTu: 0,
  soMauOcr: 0,
  boTriThuc: 0,
  tepTaiLieu: 0,
  chatbotHoatDong: 0,
  chatbotDaCauHinh: 0,
  nguoiDungHoatDong: 0,
  tongTaiKhoan: 0,
  chungTuNhap: 0,
  chungTuDaXacNhan: 0,
  chungTuDaChuyenKho: 0,
  soVaiTro: 0,
  soPhongBan: 0,
};

/** Đọc hết các trang rồi đếm — quy mô MVP nhỏ (≤500 tenant, mỗi tenant vài chục người/phòng/vai). */
async function demHet<T extends { items: unknown[]; next_cursor: string | null }>(
  duongDan: string,
  query?: Record<string, string | number>,
): Promise<number> {
  let tong = 0;
  let conTro: string | undefined;
  const thamSo = { limit: 100, ...query };
  do {
    const trang = await goiApi<T>(duongDan, {
      query: { ...thamSo, cursor: conTro },
    });
    tong += trang.items.length;
    conTro = trang.next_cursor ?? undefined;
  } while (conTro);
  return tong;
}

/**
 * Số đếm toàn hệ cho dashboard — gộp từ nhiều API của nhiều module, gọi song song.
 *
 * Đếm người dùng / phòng ban / vai trò qua phân trang vì quy mô MVP nhỏ; nếu sau
 * này cần nhanh hơn thì thêm endpoint stats phía backend.
 */
async function taiThat(): Promise<DashboardCounters> {
  const [
    statsDoc,
    statsSchema,
    statsTriThuc,
    chatbots,
    tongTaiKhoan,
    nguoiDungHoatDong,
    soPhongBan,
    soVaiTro,
  ] = await Promise.all([
    goiApi<DocStats>(`${GOC_OCR}/documents/stats`),
    goiApi<OcrSchemaStats>(`${GOC_OCR}/schemas/stats`),
    goiApi<KnowledgeStats>(`${GOC_TRI_THUC}/knowledge-bases/stats`),
    goiApi<ChatbotTroLy[]>(`${GOC_TRO_LY}/chatbots`, { query: { quan_tri: 'true' } }),
    demHet<UserPage>('/api/v1/users'),
    demHet<UserPage>('/api/v1/users', { status: 'ACTIVE' }),
    demHet<DepartmentPage>('/api/v1/departments'),
    demHet<RolePage>('/api/v1/roles'),
  ]);

  return {
    tongChungTu: statsDoc.total,
    soMauOcr: statsSchema.active_schemas,
    boTriThuc: statsTriThuc.total_knowledge_bases,
    tepTaiLieu: statsTriThuc.total_files,
    chatbotHoatDong: chatbots.filter((b) => b.active && b.answerable).length,
    chatbotDaCauHinh: chatbots.length,
    nguoiDungHoatDong,
    tongTaiKhoan,
    chungTuNhap: statsDoc.draft,
    chungTuDaXacNhan: statsDoc.confirmed,
    chungTuDaChuyenKho: statsDoc.transferred,
    soVaiTro,
    soPhongBan,
  };
}

/**
 * Danh sách bộ tri thức rút gọn cho biểu đồ phòng ban.
 *
 * API thật: gọi `GET /knowledge-bases` với `limit=100` rồi ánh xạ từ `file_counts.total`
 * và `managing_department.name` về dạng hiển thị.
 */
async function taiTriThuc(): Promise<KnowledgeBaseDisplay[]> {
  let tatCa: KbTriThuc[] = [];
  let conTro: string | undefined;
  do {
    const trang = await goiApi<TrangTriThuc<KbTriThuc>>(`${GOC_TRI_THUC}/knowledge-bases`, {
      query: { limit: 100, cursor: conTro },
    });
    tatCa = tatCa.concat(trang.items);
    conTro = trang.next_cursor ?? undefined;
  } while (conTro);

  return tatCa.map((kb) => ({
    id: kb.id,
    name: kb.name,
    managingDepartmentName: kb.managing_department?.name ?? null,
    fileCount: kb.file_counts?.total ?? 0,
  }));
}

export const dashboardRepository = {
  counters: (): Promise<DashboardCounters> =>
    dungApiThat() ? taiThat() : mockRequest(MOCK_DASHBOARD, EMPTY_COUNTERS),

  knowledgeBases: (): Promise<KnowledgeBaseDisplay[]> =>
    dungApiThat() ? taiTriThuc() : mockRequest(MOCK_KNOWLEDGE_BASES, []),
};
